// framework
import React, { useMemo, useEffect, useState, useRef } from "react";
import { audio } from "services/audioManager";
import PropTypes from "prop-types";
import { sleep } from "utils/sleep";

//styles
import styles from "./QuestionView.module.scss";

// assets
import { IMAGES, MESSAGES } from "constants/constants";

// components
import GameScreen from "components/Host/SubComponents/GameScreen/GameScreen";
import CashSlot, { buildCashMatrix } from "components/Host/SubComponents/CashSlot/CashSlot";

// constants
import { SFX, VO, MUSIC } from "constants/audio";
import { GAME_STATES } from "constants/constants";

const roundBackgroundMap = {
    1: { bg: IMAGES.HOST.questionsBackground4, answerCount: 4 },
    2: { bg: IMAGES.HOST.questionsBackground4, answerCount: 4 },
    3: { bg: IMAGES.HOST.questionsBackground4, answerCount: 4 },
    4: { bg: IMAGES.HOST.questionsBackground3, answerCount: 3 },
    5: { bg: IMAGES.HOST.questionsBackground3, answerCount: 3 },
    6: { bg: IMAGES.HOST.questionsBackground3, answerCount: 3 },
    7: { bg: IMAGES.HOST.questionsBackground2, answerCount: 2 },
};

const HINT_DELAY_MS = 20000;
const MAX_HINT_COUNT = 2; // Number of times hint can play per game

async function runAnsweringSequence({ isAnswering, hintCountRef, signal }) {
    if (!isAnswering || signal.aborted) return;

    // Play initial answering VO
    await audio.playVo(VO.TIMER_START_ANSWERING, { signal });
    if (signal.aborted) return;

    // If hint count hasn't been exceeded, schedule it
    if (hintCountRef.current < MAX_HINT_COUNT) {
        await sleep(HINT_DELAY_MS);
        if (signal.aborted) return;

        await audio.playVo(VO.ANSWERING_HINT, { signal });
        hintCountRef.current += 1;
    }
}

const REVEAL_ANSWERS_DELAY = 500;
const REVEAL_ANSWERS_INTERVAL_MS = 1200;
const READ_QUESTION_DELAY_MS = 150;
const NOTIFY_REVEAL_ANSWERS_COMPLETE_DELAY_MS = 500;

async function runRevealSequence({ isAskingQuestion, answers, questionVo, sendIfPrimary, setRevealedCount, setShowQuestion, signal, }) {
    if (!isAskingQuestion || answers.length === 0 || signal.aborted) return;

    setRevealedCount(-1);
    setShowQuestion(false); // Hide question initially

    await audio.playVo(VO.BRING_ON_ANSWERS, { timeoutMs: 5000, signal });
    if (signal.aborted) return;

    await sleep(REVEAL_ANSWERS_DELAY);

    for (let i = 0; i < answers.length; i++) {
        if (signal.aborted) return;
        audio.playSfx(SFX.ANSWER_REVEAL, { signal });
        setRevealedCount(i);
        await sleep(REVEAL_ANSWERS_INTERVAL_MS);
    }

    if (signal.aborted) return;

    await sleep(READ_QUESTION_DELAY_MS);

    if (questionVo && !signal.aborted) {
        setShowQuestion(true); // Show question when VO starts

        try {
            await audio.playVo(questionVo, { timeoutMs: 15000, signal });
        } catch (e) {
            // VO failed or was aborted; ignore
            console.warn("Question VO failed to play:", e);
        }
    }

    await sleep(NOTIFY_REVEAL_ANSWERS_COMPLETE_DELAY_MS);

    if (!signal.aborted) {
        sendIfPrimary?.(MESSAGES.TO_SERVER.QUESTION_ASKING_COMPLETE, {});
    }
}

const CASH_STACKING_INTERVAL_MS = 180;
const NOTIFY_STACKING_COMPLETE_DELAY_MS = 2000;

async function runStackingSequence({ isStacking, tallestStack, sendIfPrimary, setStackLevel, timeLeft, signal, }) {
    // If not stacking, reset and bail
    if (!isStacking || signal.aborted) {
        setStackLevel(0);
        return;
    }

    setStackLevel(0);

    // Start VO (cancellable); don't await yet
    const voRef = timeLeft === 0 ? VO.ANSWERS_TIMES_UP : VO.ANSWERS_LOCKED_IN;
    const voPromise = audio.playVo(voRef, { timeoutMs: 8000, signal });

    // Run animation as a promise
    const animationPromise = (async () => {
        for (let i = 1; i <= tallestStack; i++) {
            if (signal.aborted) return;
            await sleep(CASH_STACKING_INTERVAL_MS);
            if (signal.aborted) return;
            setStackLevel(i);
        }
    })();

    // Wait for both VO and animation to complete (or cancel)
    await Promise.all([voPromise, animationPromise]);

    await sleep(NOTIFY_STACKING_COMPLETE_DELAY_MS);

    if (!signal.aborted) {
        sendIfPrimary?.(MESSAGES.TO_SERVER.STACKING_CASH_COMPLETE, {});
    }
}

export default function QuestionView({ gameState, room, roomState, players, sendIfPrimary }) {
    const isCategorySelect = gameState === GAME_STATES.CATEGORY_SELECT;
    const isAskingQuestion = gameState === GAME_STATES.ASKING_QUESTION;
    const isAnswering = gameState === GAME_STATES.ANSWERING;
    const isStacking = gameState === GAME_STATES.STACKING_CASH;
    const showSection = isCategorySelect || isAskingQuestion || isAnswering || isStacking;

    const roundNumber = roomState?.mdmdData?.roundNumber ?? 1;
    const roundConfig = roundBackgroundMap[roundNumber] || roundBackgroundMap[1];

    const background = roundConfig.bg;
    const slotCount = roundConfig.answerCount;
    const answerSlots = useMemo(() => Array.from({ length: slotCount }), [slotCount]);
    const cashSlots = answerSlots;

    const chooserId = roomState?.mdmdData?.playerChoosingCategory;
    const playerChoosingCategory = useMemo(
        () => (players ?? []).find((p) => p?.id === chooserId) || null,
        [players, chooserId]
    );

    const timeLeft = roomState?.mdmdData?.timer ?? -1;
    const categories = roomState?.mdmdData?.categories ?? [];
    const question = roomState?.mdmdData?.question ?? "";
    const questionVo = roomState?.mdmdData?.questionVo ?? null;
    const categorySelectedIndex = roomState?.mdmdData?.categorySelectedIndex;
    const categorySelected = categories[categorySelectedIndex] ?? "";
    const answers = useMemo(() => roomState?.mdmdData?.answers ?? [], [roomState?.mdmdData?.answers]);
    const longestAnswerLen = useMemo(() => {
        if (!answers || !answers.length) return 0;
        return answers.reduce((max, a) => Math.max(max, (a ?? "").trim().length), 0);
    }, [answers]);
    const showSmallAnswers = longestAnswerLen > 25;

    const [revealedCount, setRevealedCount] = useState(-1);
    const [stackLevel, setStackLevel] = useState(0);
    const [showQuestion, setShowQuestion] = useState(false);

    // Track how many times hint has been played this game session
    const hintCountRef = useRef(0);

    // Build cash matrix using the exported function from CashSlot
    const cashMatrix = useMemo(() => {
        return buildCashMatrix(players, slotCount);
    }, [players, slotCount]);

    const tallestStack = useMemo(() => {
        let max = 0;
        for (const slot of cashMatrix) {
            for (const stackData of slot) {
                if (stackData.count > max) max = stackData.count;
            }
        }
        return max;
    }, [cashMatrix]);

    // Reset revealed count when entering/exiting major states
    useEffect(() => {
        setRevealedCount(-1);
        setShowQuestion(false);
    }, [isCategorySelect, isAskingQuestion, isAnswering, isStacking]);

    // Show question immediately when in answering or stacking states
    useEffect(() => {
        if (isAnswering || isStacking) {
            setShowQuestion(true);
        }
    }, [isAnswering, isStacking]);

    // Category Select sequence (SFX) — cancellable
    useEffect(() => {
        if (!showSection) return;
        const ac = new AbortController();
        if (isCategorySelect) {
            audio.playSfx(SFX.SHOW_CATEGORIES, { signal: ac.signal });
            audio.playVo(VO.CHOOSE_CATEGORY, { signal: ac.signal });
        } else {
            audio.playSfx(SFX.CATEGORY_SELECTED, { signal: ac.signal });
        }
        return () => ac.abort();
    }, [isCategorySelect, showSection]);

    // Asking Question: VO + reveal — cancellable
    useEffect(() => {
        const ac = new AbortController();
        runRevealSequence({ isAskingQuestion, answers, questionVo, sendIfPrimary, setRevealedCount, setShowQuestion, signal: ac.signal, });
        return () => ac.abort(); // kills VO/SFX and stops reveal loop checks
    }, [isAskingQuestion, answers, questionVo, sendIfPrimary]);

    // Stacking sequence — cancellable
    useEffect(() => {
        const ac = new AbortController();
        runStackingSequence({ isStacking, tallestStack, sendIfPrimary, setStackLevel, timeLeft, signal: ac.signal, });
        return () => ac.abort(); // kills VO and stops stacking loop checks
    }, [isStacking, sendIfPrimary, timeLeft]);

    // Play background music while this component is visible
    useEffect(() => {
        if (!showSection) return;
        const ac = new AbortController();
        audio.playMusic(MUSIC.ASKING_QUESTION_LOOP, { loop: true, fadeMs: 500, signal: ac.signal, });
        return () => ac.abort();
    }, [showSection]);

    // Answering started VO with hint sequence
    useEffect(() => {
        if (!isAnswering) return;
        const ac = new AbortController();
        runAnsweringSequence({ isAnswering, hintCountRef, signal: ac.signal });
        return () => ac.abort();
    }, [isAnswering]);

    // Timer VO
    useEffect(() => {
        if (timeLeft === 10) {
            const vo = isAnswering ? VO.ANSWERING_TIMES_RUNNING_OUT : VO.CATEGORY_TIMES_RUNNING_OUT
            audio.playVo(vo);
        }

        if (timeLeft === 0 && isCategorySelect) {
            audio.playVo(VO.CATEGORY_TIMES_UP);
        }
    }, [timeLeft, isAnswering, isCategorySelect]);

    const prevLockedInCountRef = useRef(0);
    useEffect(() => {
        if (!isAnswering && !isStacking) {
            prevLockedInCountRef.current = 0;
            return;
        }

        const lockedInCount = players?.filter(p => p?.mdmdData?.lockedIn).length ?? 0;
        if (lockedInCount > prevLockedInCountRef.current) {
            audio.playSfx(SFX.PLAYER_LOCKED_IN); // Replace with your actual SFX constant
        }

        prevLockedInCountRef.current = lockedInCount;
    }, [isAnswering, isStacking, players]);

    return (
        <section className={styles.questionView} data-show={showSection ? "true" : "false"} data-testid="question-view" >
            <img src={background} alt="" aria-hidden="true" className={styles.background} />
            <GameScreen className={styles.screen}>
                {/* Category Select */}
                <div className={styles.categoriesRoot} data-testid="category-select" data-show={isCategorySelect ? "true" : "false"} >
                    <div className={`${styles.panel} ${styles.tile} ${styles.chooser}`} role="status" aria-live="polite">
                        <span className={styles.label}>
                            {playerChoosingCategory ? `${playerChoosingCategory.name} is choosing a category` : "Waiting for player to choose a category"}
                        </span>
                    </div>
                    <div className={styles.categories}>
                        {categories.map((c, i) => (
                            <div key={`${c}-${i}`} className={`${styles.panel} ${styles.tile}`}>
                                <span className={styles.label}>{c}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Question */}
                <div className={styles.questionRoot} data-testid="question-view-panel" data-show={isAskingQuestion || isAnswering || isStacking ? "true" : "false"} >

                    <div className={`${styles.panel} ${styles.tile} ${styles.chosenCategory}`} role="status" aria-live="polite">
                        <span className={styles.label}>{categorySelected}</span>
                    </div>

                    <div className={`${styles.panel} ${styles.questionPanel}`} data-show-question={showQuestion ? "true" : "false"}>
                        <span className={styles.questionText} title={question}>
                            {question || "…"}
                        </span>
                    </div>
                </div>
            </GameScreen>

            {/* Answer tiles (text reveals) */}
            <div className={styles.answersRoot} data-count={slotCount}>
                {answerSlots.map((_, i) => (
                    <div key={`ans-${i}`} className={`${styles.panel} ${styles.answerTile}`} data-show={isAnswering || isStacking || i <= revealedCount ? "true" : "false"} style={{ "--answer-bg": `url(${IMAGES.HOST.answerBackground})` }} >
                        <span className={`${styles.answerLabel} ${showSmallAnswers ? styles.small : ""}`}>
                            {answers[i] ?? ""}
                        </span>
                    </div>
                ))}
            </div>

            {/* Cash slots */}
            <div className={styles.cashRoot} data-count={slotCount}>
                {cashSlots.map((_, slotIdx) => (
                    <CashSlot key={`cash-slot-${slotIdx}`} className={styles.cashSlot} slotIdx={slotIdx} slotCount={slotCount}
                        cashMatrix={cashMatrix} stackLevel={stackLevel} gameState={GAME_STATES.STACKING_CASH} yOffset="-76%"/>
                ))}
            </div>
        </section>
    );
}

QuestionView.propTypes = {
    gameState: PropTypes.any,
    room: PropTypes.object,
    roomState: PropTypes.object,
    players: PropTypes.array,
};